import { gql } from '@apollo/client';
import { apolloClient } from '../graphql/client';
import aiService from './aiService';

class CategoryService {
  // Get all categories
  async getCategories() {
    const query = gql`
      query GetCategories {
        categories {
          id
          code
          parentCode
          name
          description
          audioText
          source
          status
          createdDate
          updatedDate
          imageData
          imageFileName
          imageContentType
          imageGenerated
          imageGeneratedAt
          isActive
          soundOptions {
            voiceId
            soundData
            soundFileName
            soundContentType
            soundGenerated
            soundGeneratedAt
          }
        }
      }
    `;

    try {
      console.log('Attempting to query GraphQL at:', process.env.REACT_APP_GRAPHQL_ENDPOINT);
      const result = await apolloClient.query({
        query,
        fetchPolicy: 'network-only'
      });
      console.log('GraphQL response:', result);
      return result.data.categories;
    } catch (error: any) {
      console.error('Error fetching categories:', error);
      console.error('Network error:', error.networkError);
      console.error('GraphQL errors:', error.graphQLErrors);
      
      if (error.networkError) {
        throw new Error(`Failed to connect to GraphQL API: ${error.networkError.message}`);
      } else if (error.graphQLErrors && error.graphQLErrors.length > 0) {
        throw new Error(`GraphQL error: ${error.graphQLErrors[0].message}`);
      } else {
        throw new Error(`Failed to fetch categories: ${error.message}`);
      }
    }
  }

  // Get category by ID
  async getCategoryById(id: string) {
    const query = gql`
      query GetCategoryById($id: ID!) {
        categoryById(id: $id) {
          id
          code
          parentCode
          name
          description
          audioText
          source
          status
          createdDate
          updatedDate
          imageData
          imageFileName
          imageContentType
          imageGenerated
          isActive
          soundOptions {
            voiceId
            soundData
            soundFileName
            soundContentType
            soundGenerated
            soundGeneratedAt
          }
        }
      }
    `;

    try {
      const result = await apolloClient.query({
        query,
        variables: { id },
        fetchPolicy: 'network-only'
      });
      return result.data.categoryById;
    } catch (error) {
      console.error('Error fetching category by ID:', error);
      throw error;
    }
  }

  // Create a new category or reactivate existing one with same code
  async createCategory(categoryData: {
    code: string;
    name: string;
    description?: string;
    parentCode?: string;
    audioText?: string;
    source?: number;
    status?: number;
    imageData?: string; // base64 encoded
    imageFileName?: string;
    imageContentType?: string;
    soundOptions?: any[]; // Array of sound options
  }) {
    try {
      // Step 1: Check if a category with the same code already exists
      console.log('🔍 Checking if category with code exists:', categoryData.code);
      
      let existingCategories;
      try {
        existingCategories = await this.getCategories();
        console.log('✅ Successfully fetched categories. Total categories found:', existingCategories.length);
        console.log('📋 Category codes in database:', existingCategories.map((c: any) => ({ code: c.code, status: c.status, id: c.id })));
      } catch (fetchError) {
        console.error('❌ Failed to fetch existing categories:', fetchError);
        throw new Error(`Failed to check for existing categories: ${fetchError instanceof Error ? fetchError.message : 'Unknown error'}`);
      }
      
      const existingCategory = existingCategories.find((c: any) => c.code === categoryData.code);
      console.log('🔎 Looking for existing category with code:', categoryData.code);
      console.log('🎯 Found existing category?', existingCategory ? 'YES' : 'NO');
      
      if (existingCategory) {
        console.log('🎉 Found existing category with code:', categoryData.code, 'ID:', existingCategory.id, 'Current status:', existingCategory.status);
        console.log('📝 Will UPDATE existing category instead of creating new one');
        
        // Category exists - update it with ALL new data and set status to 1 (active)
        // Clean soundOptions to remove __typename which causes GraphQL input errors
        const cleanedExistingSoundOptions = existingCategory.soundOptions?.map((option: any) => {
          const { __typename, ...cleanOption } = option;
          return cleanOption;
        }) || [];
        
        const updateData = {
          code: categoryData.code, // Keep the same code
          name: categoryData.name, // Update name
          description: categoryData.description || existingCategory.description, // Update description or keep existing
          parentCode: categoryData.parentCode || existingCategory.parentCode, // Update parentCode or keep existing
          audioText: categoryData.audioText || existingCategory.audioText, // Update audioText or keep existing
          source: categoryData.source ?? existingCategory.source ?? 1, // Update source or keep existing or default to 1
          status: 1, // Always set to active when reactivating
          // Preserve existing media data unless new data is provided
          imageData: categoryData.imageData || existingCategory.imageData,
          imageFileName: categoryData.imageFileName || existingCategory.imageFileName,
          imageContentType: categoryData.imageContentType || existingCategory.imageContentType,
          soundOptions: categoryData.soundOptions || cleanedExistingSoundOptions
        };
        
        console.log('📤 Updating existing category with all new data:', updateData);
        const updatedCategory = await this.updateCategory(existingCategory.id, updateData);
        
        console.log('✅ Existing category reactivated and updated successfully');
        return {
          ...updatedCategory,
          _wasReactivated: true // Add metadata to indicate this was a reactivation
        };
      }
      
      // Step 2: No existing category found, create new one
      console.log('🆕 No existing category found, creating new one');
      
      // Send only required fields and basic optional fields to avoid backend issues
      const categoryToCreate = {
        code: categoryData.code,
        name: categoryData.name,
        description: categoryData.description || undefined,
        parentCode: categoryData.parentCode || undefined,
        audioText: categoryData.audioText || undefined,
        source: categoryData.source ?? 1, // Default to 1
        status: categoryData.status ?? 1, // Default to active
        // Explicitly set optional fields to undefined if not provided
        imageData: categoryData.imageData || undefined,
        imageFileName: categoryData.imageFileName || undefined,
        imageContentType: categoryData.imageContentType || undefined,
        soundOptions: categoryData.soundOptions || [] // Empty array if not provided
      };
      
      console.log('Category data being sent to backend:', categoryToCreate);
      
      const mutation = gql`
        mutation CreateCategory($category: CategoryInput!) {
          createCategory(category: $category) {
            id
            code
            parentCode
            name
            description
            audioText
            source
            status
            createdDate
            updatedDate
            imageData
            imageFileName
            imageContentType
            imageGenerated
            imageGeneratedAt
            isActive
            soundOptions {
              voiceId
              soundData
              soundFileName
              soundContentType
              soundGenerated
              soundGeneratedAt
            }
          }
        }
      `;

      console.log('Sending GraphQL mutation with variables:', { category: categoryToCreate });
      
      const result = await apolloClient.mutate({
        mutation,
        variables: { category: categoryToCreate }
      });
      
      console.log('✅ New category created successfully');
      return {
        ...result.data.createCategory,
        _wasReactivated: false // Add metadata to indicate this was a new creation
      };
      
    } catch (error: any) {
      console.error('Error creating/updating category:', error);
      console.error('Error details:', {
        message: error.message,
        graphQLErrors: error.graphQLErrors,
        networkError: error.networkError,
        extraInfo: error.extraInfo
      });
      
      // Check if this is a duplicate code error from the backend
      if (error instanceof Error && error.message.includes('already exists')) {
        console.log('Backend detected duplicate code, attempting to find and update existing category');
        // Retry the check in case we missed it due to timing
        try {
          console.log('🔄 Retrying category check after backend duplicate error...');
          const existingCategories = await this.getCategories();
          console.log('🔄 Retry - Total categories found:', existingCategories.length);
          const existingCategory = existingCategories.find((c: any) => c.code === categoryData.code);
          
          if (existingCategory) {
            console.log('Found existing category on retry, updating with all fields:', existingCategory.id);
            // Clean soundOptions to remove __typename which causes GraphQL input errors
            const cleanedExistingSoundOptions = existingCategory.soundOptions?.map((option: any) => {
              const { __typename, ...cleanOption } = option;
              return cleanOption;
            }) || [];
            
            const updateData = {
              code: categoryData.code, // Keep the same code
              name: categoryData.name, // Update name
              description: categoryData.description || existingCategory.description, // Update description or keep existing
              parentCode: categoryData.parentCode || existingCategory.parentCode, // Update parentCode or keep existing
              audioText: categoryData.audioText || existingCategory.audioText, // Update audioText or keep existing
              source: categoryData.source ?? existingCategory.source ?? 1, // Update source or keep existing or default to 1
              status: 1, // Always set to active when reactivating
              // Preserve existing media data unless new data is provided
              imageData: categoryData.imageData || existingCategory.imageData,
              imageFileName: categoryData.imageFileName || existingCategory.imageFileName,
              imageContentType: categoryData.imageContentType || existingCategory.imageContentType,
              soundOptions: categoryData.soundOptions || cleanedExistingSoundOptions
            };
            
            const updatedCategory = await this.updateCategory(existingCategory.id, updateData);
            return {
              ...updatedCategory,
              _wasReactivated: true
            };
          }
        } catch (retryError) {
          console.error('Retry update also failed:', retryError);
        }
      }
      
      throw error;
    }
  }

  // Update an existing category
  async updateCategory(id: string, categoryData: {
    code?: string;
    name?: string;
    description?: string;
    parentCode?: string;
    audioText?: string;
    source?: number;
    status?: number;
    imageData?: string; // base64 encoded
    imageFileName?: string;
    imageContentType?: string;
    soundOptions?: any[]; // Array of sound options
  }) {
    const mutation = gql`
      mutation UpdateCategory($id: ID!, $category: CategoryInput!) {
        updateCategory(id: $id, category: $category) {
          id
          code
          parentCode
          name
          description
          audioText
          source
          status
          createdDate
          updatedDate
          imageData
          imageFileName
          imageContentType
          imageGenerated
          imageGeneratedAt
          isActive
          soundOptions {
            voiceId
            soundData
            soundFileName
            soundContentType
            soundGenerated
            soundGeneratedAt
          }
        }
      }
    `;

    try {
      const result = await apolloClient.mutate({
        mutation,
        variables: { id, category: categoryData }
      });
      return result.data.updateCategory;
    } catch (error) {
      console.error('Error updating category:', error);
      throw error;
    }
  }

  // Delete a category
  async deleteCategory(id: string) {
    const mutation = gql`
      mutation DeleteCategory($id: ID!) {
        deleteCategory(id: $id)
      }
    `;

    try {
      const result = await apolloClient.mutate({
        mutation,
        variables: { id }
      });
      return result.data.deleteCategory;
    } catch (error) {
      console.error('Error deleting category:', error);
      throw error;
    }
  }

  // Toggle category status (activate/deactivate)
  async toggleCategoryStatus(id: string, status: number) {
    const mutation = gql`
      mutation UpdateCategory($id: ID!, $category: CategoryInput!) {
        updateCategory(id: $id, category: $category) {
          id
          status
          isActive
        }
      }
    `;

    try {
      const result = await apolloClient.mutate({
        mutation,
        variables: { 
          id, 
          category: { status } 
        }
      });
      return result.data.updateCategory;
    } catch (error) {
      console.error('Error toggling category status:', error);
      throw error;
    }
  }

  // Generate audio for category using GraphQL mutation
  async generateAudio(categoryIdOrCode: string, text?: string): Promise<{success: boolean, message: string, audioUrl?: string}> {
    const generateAudioMutation = gql`
      mutation GenerateCategoryAudio($id: ID!, $text: String) {
        generateCategoryAudio(id: $id, text: $text) {
          id
          code
          name
          soundOptions {
            voiceId
            soundData
            soundFileName
            soundContentType
            soundGenerated
            soundGeneratedAt
          }
        }
      }
    `;

    try {
      console.log('Generating audio for category:', categoryIdOrCode, 'with text:', text);
      
      // If categoryIdOrCode is actually a code, we need to find the ID first
      let categoryId = categoryIdOrCode;
      if (!categoryIdOrCode.match(/^[0-9a-f]{24}$/i)) {
        // This looks like a code, not an ID, so find the category by code
        const categories = await this.getCategories();
        const category = categories.find((c: any) => c.code === categoryIdOrCode);
        if (!category) {
          throw new Error(`Category with code ${categoryIdOrCode} not found`);
        }
        categoryId = category.id;
      }
      
      const result = await apolloClient.mutate({
        mutation: generateAudioMutation,
        variables: { id: categoryId, text }
      });
      
      const updatedCategory = result.data.generateCategoryAudio;
      console.log('✅ Audio generation completed successfully for category:', updatedCategory.name);
      
      // Return the first sound option as audioUrl for preview
      let audioUrl = undefined;
      if (updatedCategory.soundOptions && updatedCategory.soundOptions.length > 0) {
        const soundOption = updatedCategory.soundOptions[0];
        audioUrl = `data:${soundOption.soundContentType || 'audio/mpeg'};base64,${soundOption.soundData}`;
      }
      
      return {
        success: true,
        message: `Audio generated successfully for ${updatedCategory.soundOptions?.length || 0} voice(s)`,
        audioUrl
      };
      
    } catch (error) {
      console.error('❌ Error generating audio for category:', error);
      return {
        success: false,
        message: `Failed to generate audio: ${error instanceof Error ? error.message : 'Unknown error'}`
      };
    }
  }

  // Generate and save image for category using GraphQL mutation
  async generateImage(categoryId: string, prompt?: string): Promise<{success: boolean, message: string, imageUrl?: string}> {
    const generateImageMutation = gql`
      mutation GenerateCategoryImage($id: ID!, $prompt: String) {
        generateCategoryImage(id: $id, prompt: $prompt) {
          id
          code
          name
          imageData
          imageFileName
          imageContentType
          imageGenerated
          imageGeneratedAt
        }
      }
    `;

    try {
      console.log('Generating image for category:', categoryId, 'with prompt:', prompt);
      
      const result = await apolloClient.mutate({
        mutation: generateImageMutation,
        variables: { id: categoryId, prompt }
      });
      
      const updatedCategory = result.data.generateCategoryImage;
      console.log('✅ Image generation completed successfully for category:', updatedCategory.name);
      
      // Return the image as data URL for preview
      let imageUrl = undefined;
      if (updatedCategory.imageData) {
        imageUrl = `data:${updatedCategory.imageContentType || 'image/png'};base64,${updatedCategory.imageData}`;
      }
      
      return {
        success: true,
        message: 'Image generated and saved successfully',
        imageUrl
      };
      
    } catch (error) {
      console.error('❌ Error generating image for category:', error);
      return {
        success: false,
        message: `Failed to generate image: ${error instanceof Error ? error.message : 'Unknown error'}`
      };
    }
  }
}

export const categoryService = new CategoryService();
export default categoryService;
